import { Button, Card, Group, Pagination, Table, Text, Textarea, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconPlayerPlay, IconX } from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { CampaignTypes } from '../../constants/pageTitles';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import ConfirmationModal from '../Modals/ConfirmationModal';
import Spinner from '../Spinner';

const PAGE_SIZE = 20;

export const EDITOR_PATHS = { simple: 'campaigns', workflow: 'workflows' };
const SECTION_NAMES = { simple: 'Campaigns', workflow: 'Workflows' };

const formatDate = (value) => new Date(value).toLocaleString();

const launchMessage = ({ name, scheduledAt, segment }) =>
  `This starts ${name} ${scheduledAt ? `on ${formatDate(scheduledAt)}` : 'now'}${segment ? ` for ${segment.name}` : ''}.`;

const AiDraftsList = ({ type, emptyText }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const [page, setPage] = useState(1);
  const [reloads, setReloads] = useState(0);
  const [result, setResult] = useState();
  const [error, setError] = useState();
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [decisionError, setDecisionError] = useState();

  const domainPath = `/accounts/${accountId}/domains/${domainId}`;
  const draftsUrl = `/api/admin${domainPath}/aiDrafts`;

  useEffect(() => {
    let cancelled = false;
    setResult(undefined);
    setError(undefined);
    const query = queryString.stringify({ type, status: 'pending', page, pageSize: PAGE_SIZE });
    fetch(`${draftsUrl}?${query}`)
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, draftsUrl, type, page, reloads]);

  const closeDecision = () => {
    setDecision(null);
    setNote('');
    setDecisionError(undefined);
  };

  const decide = (close) => {
    const { action, draft } = decision;
    setDeciding(true);
    setDecisionError(undefined);
    const approving = action !== 'reject';
    fetch(`${draftsUrl}/${draft.id}/${approving ? 'approve' : 'reject'}`, {
      method: 'POST',
      body: JSON.stringify({
        ...(approving && { updatedAt: draft.updatedAt, launch: action === 'approve' }),
        note: note.trim() || undefined,
      }),
    })
      .then(() => {
        if (action === 'approve') {
          notifications.show({
            title: 'Draft launched',
            message: `${draft.name} was approved and started.`,
            color: 'green',
          });
        }
        if (action === 'accept') {
          notifications.show({
            title: 'Draft approved',
            message: `${draft.name} was approved and is not running. Launch it from ${SECTION_NAMES[type]} when you are ready.`,
            color: 'green',
          });
        }
        if (result.drafts.length === 1 && page > 1) {
          setPage(page - 1);
        } else if (result.total > result.drafts.length) {
          setReloads((count) => count + 1);
        } else {
          setResult(({ drafts, total }) => ({ drafts: drafts.filter(({ id }) => id !== draft.id), total: total - 1 }));
        }
        close();
      })
      .catch((err) => {
        if (approving) {
          notifications.show({
            title: action === 'approve' ? 'Draft not launched' : 'Draft not approved',
            message: getErrorMessage(err),
            color: 'red',
          });
          setReloads((count) => count + 1);
          closeDecision();
        } else {
          setDecisionError(new Error(getErrorMessage(err)));
        }
      })
      .finally(() => setDeciding(false));
  };

  let content;
  if (error) {
    content = <ErrorMessage message={getErrorMessage(error)} />;
  } else if (!result) {
    content = <Spinner />;
  } else if (result.drafts.length === 0) {
    content = (
      <Text c="dimmed" size="sm">
        {emptyText}
      </Text>
    );
  } else {
    content = (
      <>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Mode</Table.Th>
              <Table.Th>Segment</Table.Th>
              <Table.Th>Schedule</Table.Th>
              <Table.Th>Proposed by</Table.Th>
              <Table.Th>Proposed</Table.Th>
              <Table.Th>Rationale</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {result.drafts.map((draft) => (
              <Table.Tr key={draft.id}>
                <Table.Td>{draft.name}</Table.Td>
                <Table.Td>{CampaignTypes[draft.mode]}</Table.Td>
                <Table.Td>{draft.segment?.name || 'None'}</Table.Td>
                <Table.Td>{draft.scheduledAt ? formatDate(draft.scheduledAt) : 'On approval'}</Table.Td>
                <Table.Td>
                  <Text size="sm">AI assistant</Text>
                  {draft.proposedBy && (
                    <Text size="xs" c="dimmed">
                      {draft.proposedBy.email}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{formatDate(draft.proposedAt)}</Table.Td>
                <Table.Td>
                  <Tooltip label={draft.rationale} multiline w={400} withArrow disabled={!draft.rationale}>
                    <Text size="sm" lineClamp={2} maw={360}>
                      {draft.rationale}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Button
                      component="a"
                      href={`#${domainPath}/${EDITOR_PATHS[type]}/${draft.id}/details?mode=${draft.mode}`}
                      size="xs"
                      variant="default"
                    >
                      Open
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconCheck size={14} />}
                      onClick={() => setDecision({ action: 'accept', draft })}
                    >
                      Approve
                    </Button>
                    <Button
                      size="xs"
                      leftSection={<IconPlayerPlay size={14} />}
                      onClick={() => setDecision({ action: 'approve', draft })}
                    >
                      Approve and launch
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      leftSection={<IconX size={14} />}
                      onClick={() => setDecision({ action: 'reject', draft })}
                    >
                      Reject
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {result.total > PAGE_SIZE && (
          <Pagination total={Math.ceil(result.total / PAGE_SIZE)} value={page} onChange={setPage} mt="md" />
        )}
      </>
    );
  }

  return (
    <>
      <Card withBorder>{content}</Card>
      <ConfirmationModal
        show={decision !== null}
        close={closeDecision}
        confirm={decide}
        loading={deciding}
        error={decisionError}
        message={
          decision &&
          {
            approve: launchMessage(decision.draft),
            accept: `This approves ${decision.draft.name} and keeps it as a draft that is not running.`,
            reject: `This deletes the draft ${decision.draft.name}.`,
          }[decision.action]
        }
      >
        <Textarea
          label="Note"
          description="Optional."
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          autosize
          minRows={2}
        />
      </ConfirmationModal>
    </>
  );
};

export default AiDraftsList;
